import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import equal from "fast-deep-equal";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import _Upscope from "./sdk.js";
const UpscopeContext = createContext({
    Upscope: undefined,
    shortId: undefined,
    async getLookupCode() {
        return "";
    },
    listen: (event, callback) => { },
    reset: () => { },
    isSharing: false,
});
export function useUpscope() {
    return useContext(UpscopeContext);
}
export function UpscopeProvider({ children, enabled, test, ...configProps }) {
    const Upscope = useMemo(() => (...args) => {
        if (test)
            return; // Do not use Upscope SDK on test
        return _Upscope(...args);
    }, [test]);
    const [loaded, setLoaded] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [shortId, setShortId] = useState(undefined);
    const [currentConfiguration, setCurrentConfiguration] = useState(undefined);
    const upscopeActive = enabled && loaded;
    const [pendingConfigurationUpdate, setPendingConfigurationUpdate] = useState(null);
    enabled ??= true;
    useEffect(() => {
        if (currentConfiguration === undefined || !upscopeActive) {
            setPendingConfigurationUpdate(null);
            return;
        }
        const config = Object.entries(configProps)
            .filter(([configName, configValue]) => configName !== "regionWhitelist" &&
            !equal(configValue, currentConfiguration[configName]))
            .reduce((config, [configName, configValue]) => {
            config[configName] = configValue;
            return config;
        }, {});
        setPendingConfigurationUpdate(Object.keys(config).length === 0 ? null : config);
    }, [currentConfiguration, configProps, upscopeActive]);
    useEffect(() => {
        if (!currentConfiguration)
            return;
        const isEnabledFirstTime = enabled && !loaded;
        const isDisabledAfterInit = !enabled && loaded;
        if (isEnabledFirstTime) {
            Upscope("init", currentConfiguration);
            setLoaded(true);
        }
        else if (isDisabledAfterInit) {
            Upscope("reset", false);
        }
    }, [enabled, loaded, currentConfiguration, Upscope]);
    useEffect(() => {
        if (upscopeActive) {
            Upscope("getShortId", (shortId) => {
                setShortId(shortId);
            });
        }
        else {
            setShortId(undefined);
        }
    }, [upscopeActive, Upscope]);
    useEffect(() => {
        if (!upscopeActive || !pendingConfigurationUpdate || !currentConfiguration)
            return;
        Upscope("updateConnection", pendingConfigurationUpdate);
        setPendingConfigurationUpdate(null);
        const newCurrentConfiguration = { ...currentConfiguration, ...pendingConfigurationUpdate };
        setCurrentConfiguration(newCurrentConfiguration);
    }, [upscopeActive, pendingConfigurationUpdate, currentConfiguration, Upscope]);
    useEffect(() => {
        if (currentConfiguration || !enabled)
            return;
        // Initialize configurations
        setCurrentConfiguration(configProps);
    }, [enabled, currentConfiguration, configProps]);
    useEffect(() => {
        if (!loaded)
            return;
        Upscope("on", "sessionStart", () => {
            setSharing(true);
        });
        Upscope("on", "sessionContinue", () => {
            setSharing(true);
        });
        Upscope("on", "sessionEnd", () => {
            setSharing(false);
        });
    }, [loaded, Upscope]);
    const getLookupCode = useCallback(() => {
        return new Promise((resolve) => {
            if (!upscopeActive)
                return resolve(undefined);
            Upscope("getLookupCode", (lookupCode) => {
                resolve(lookupCode);
            });
        });
    }, [upscopeActive, Upscope]);
    const listen = useCallback((event, callback) => {
        Upscope("on", event, callback);
    }, [Upscope]);
    const reset = useCallback(() => {
        Upscope("reset", true);
    }, [Upscope]);
    return (_jsx(UpscopeContext.Provider, { value: {
            Upscope,
            shortId,
            getLookupCode,
            listen,
            reset,
            isSharing: sharing,
        }, children: children }));
}
export function Masked({ children }) {
    const beforeRef = useRef(null);
    const afterRef = useRef(null);
    useEffect(() => {
        if (beforeRef.current && afterRef.current) {
            let nextSibling = beforeRef.current.nextSibling;
            while (nextSibling && nextSibling !== afterRef.current) {
                nextSibling.dataset.upscopeMasked = true;
                nextSibling = nextSibling.nextSibling;
            }
        }
    }, [beforeRef, afterRef, children]);
    return (_jsxs(_Fragment, { children: [_jsx("script", { ref: beforeRef }), children, _jsx("script", { ref: afterRef })] }));
}
export function NoRemoteControl({ children }) {
    const beforeRef = useRef(null);
    const afterRef = useRef(null);
    useEffect(() => {
        if (beforeRef.current && afterRef.current) {
            let nextSibling = beforeRef.current.nextSibling;
            while (nextSibling && nextSibling !== afterRef.current) {
                nextSibling.dataset.upscopeNoRemoteControl = true;
                nextSibling = nextSibling.nextSibling;
            }
        }
    }, [beforeRef, afterRef, children]);
    return (_jsxs(_Fragment, { children: [_jsx("script", { ref: beforeRef }), children, _jsx("script", { ref: afterRef })] }));
}
